import React, { useEffect, useState } from 'react';
import { BOAPI } from '../service/UsersService';
import ConfirmModal from './ConfirmModal';

// טאב ניהול קטגוריות בתי עסק (BusinessCategories) - עריכת שם וסדר תצוגה,
// הוספה והשבתה. ה-key הטכני לא משתנה בעריכה, אז שינוי שם מתעדכן מיד בכל
// בתי העסק המשויכים. "מחיקה" = השבתה (active=0) לפי המוסכמה בפרויקט - קטגוריה
// מושבתת לא מוצגת באפליקציה ולא ניתנת לבחירה, הקישורים הקיימים נשמרים.
// אותה מוסכמת מסך כמו FoodTypesTab.jsx
const normalize = (s) => (s || '').trim().replace(/\s+/g, ' ');

// אייקונים לבחירה (שמות Ionicons - כך הם מוצגים באפליקציה). הבק אופיס לא
// מצייר אותם, רק שומר את השם
const ICON_OPTIONS = [
  ['', 'ללא אייקון'],
  ['shirt-outline', 'חולצה'], ['book-outline', 'ספר'], ['watch-outline', 'שעון'], ['glasses-outline', 'משקפיים'],
  ['sparkles-outline', 'נצנוץ'], ['gift-outline', 'מתנה'], ['wine-outline', 'כוס'], ['diamond-outline', 'יהלום'],
  ['footsteps-outline', 'צעדים'], ['heart-outline', 'לב'], ['briefcase-outline', 'תיק עבודה'], ['home-outline', 'בית'], ['restaurant-outline', 'מזון'], ['cart-outline', 'עגלה'],
  ['ellipsis-horizontal-circle-outline', 'שונות'],
];
const iconLabel = (key) => (ICON_OPTIONS.find(([k]) => k === key) || [key, key])[1];

const BusinessCategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newIsGroup, setNewIsGroup] = useState(false);
  const [newParentKey, setNewParentKey] = useState('');
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ label: '', sortOrder: '0', parentKey: '', icon: '', color: '' });
  const [busyId, setBusyId] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  // חיפוש לפי שם + מיון תצוגה: 'order' (סדר תצוגה, כמו באפליקציה) | 'alpha' (א-ב)
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState('order');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await BOAPI.fetchAllBusinessCategories();
      if (resp?.[0]?.[0]?.code === 0) setCategories(resp[1] || []);
      else setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת הקטגוריות');
    } catch {
      setError('שגיאה בטעינת הקטגוריות');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sortList = (list) => [...list].sort((a, b) => (a.sortOrder - b.sortOrder) || a.label.localeCompare(b.label, 'he'));

  const openAddModal = () => {
    setNewLabel('');
    setNewIsGroup(false);
    setNewParentKey('');
    setAddError('');
    setAddModalOpen(true);
  };

  const addCategory = async () => {
    const label = normalize(newLabel);
    if (!label) {
      setAddError('יש להזין שם קטגוריה');
      return;
    }
    if (categories.some((c) => normalize(c.label).toLowerCase() === label.toLowerCase())) {
      setAddError('כבר קיימת קטגוריה בשם הזה');
      return;
    }
    setAdding(true);
    setAddError('');
    try {
      const resp = await BOAPI.addBusinessCategory({ label, isGroup: newIsGroup ? 1 : 0, parentKey: newIsGroup ? null : newParentKey || null });
      if (resp?.[0]?.[0]?.code === 0) {
        await load();
        setAddModalOpen(false);
        setNewLabel('');
      } else {
        setAddError(resp?.[0]?.[0]?.msg || 'ההוספה נכשלה');
      }
    } catch {
      setAddError('שגיאה בתקשורת עם השרת');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditDraft({ label: c.label, sortOrder: String(c.sortOrder ?? 0), parentKey: c.parentKey || '', icon: c.icon || '', color: c.color || '' });
  };

  const saveEdit = async (id) => {
    const label = normalize(editDraft.label);
    if (!label) return;
    if (categories.some((c) => c.id !== id && normalize(c.label).toLowerCase() === label.toLowerCase())) {
      setError('כבר קיימת קטגוריה בשם הזה');
      return;
    }
    const sortOrderNum = Number(editDraft.sortOrder);
    setBusyId(id);
    setError('');
    try {
      const resp = await BOAPI.updateBusinessCategory({
        id, label, sortOrder: isNaN(sortOrderNum) ? 0 : sortOrderNum,
        parentKey: editDraft.parentKey || null, icon: editDraft.icon || null, color: editDraft.color || null,
      });
      if (resp?.[0]?.[0]?.code === 0) {
        setCategories((prev) => sortList(prev.map((c) => (c.id === id ? resp[1][0] : c))));
        setEditingId(null);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (c) => {
    setConfirmDeactivate(null);
    setBusyId(c.id);
    setError('');
    try {
      const resp = await BOAPI.toggleBusinessCategoryActive(c.id);
      if (resp?.[0]?.[0]?.code === 0) {
        const { active } = resp[1][0];
        setCategories((prev) => prev.map((x) => (x.id === c.id ? { ...x, active } : x)));
      } else {
        setError(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setBusyId(null);
    }
  };

  // השבתת קטגוריה שיש בה בתי עסק - מבקשים אישור (הם יפסיקו להופיע בסינון שלה)
  const onToggleClick = (c) => {
    if (c.active && c.businessCount > 0) setConfirmDeactivate(c);
    else toggleActive(c);
  };

  const activeCount = categories.filter((c) => c.active).length;
  const groups = categories.filter((c) => c.isGroup);
  const groupLabel = (key) => groups.find((g) => g.key === key)?.label || key;

  const q = normalize(search).toLowerCase();
  const visibleCategories = categories
    .filter((c) => !q || c.label.toLowerCase().includes(q))
    .sort((a, b) => (sortMode === 'alpha'
      ? a.label.localeCompare(b.label, 'he')
      : (a.sortOrder - b.sortOrder) || a.label.localeCompare(b.label, 'he')));

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>קטגוריות בתי עסק</h2>
        <div style={s.headerRight}>
          <span style={s.totalBadge}>{activeCount} פעילות</span>
          <button style={s.addBtn} onClick={openAddModal}>+ קטגוריה חדשה</button>
        </div>
      </div>
      <p style={s.subtitle}>שינוי שם מתעדכן מיד בכל בתי העסק המשויכים. סדר תצוגה נמוך מופיע ראשון באפליקציה.</p>

      <div style={s.toolbar}>
        <input
          type="text"
          style={s.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חיפוש קטגוריה..."
        />
        <div style={s.sortToggle}>
          <button
            type="button"
            style={{ ...s.sortBtn, ...(sortMode === 'order' ? s.sortBtnActive : {}) }}
            onClick={() => setSortMode('order')}
          >
            לפי סדר תצוגה
          </button>
          <button
            type="button"
            style={{ ...s.sortBtn, ...(sortMode === 'alpha' ? s.sortBtnActive : {}) }}
            onClick={() => setSortMode('alpha')}
          >
            א-ב
          </button>
        </div>
      </div>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}

      {loading ? (
        <div style={s.center}>טוען...</div>
      ) : categories.length === 0 ? (
        <div style={s.emptyState}>אין עדיין קטגוריות</div>
      ) : visibleCategories.length === 0 ? (
        <div style={s.emptyState}>לא נמצאו קטגוריות עבור "{search.trim()}"</div>
      ) : (
        <div style={s.grid}>
          {visibleCategories.map((c) => (
            <div key={c.id} style={{ ...s.card, ...(c.active ? {} : s.cardInactive) }}>
              {editingId === c.id ? (
                <div style={s.editRow}>
                  <input
                    type="text"
                    style={{ ...s.editInput, flex: 1 }}
                    value={editDraft.label}
                    onChange={(e) => setEditDraft((p) => ({ ...p, label: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && saveEdit(c.id)}
                    maxLength={50}
                    autoFocus
                  />
                  <input
                    type="number"
                    style={{ ...s.editInput, width: 64 }}
                    value={editDraft.sortOrder}
                    onChange={(e) => setEditDraft((p) => ({ ...p, sortOrder: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && saveEdit(c.id)}
                    title="סדר תצוגה"
                  />
                </div>
              ) : (
                <div style={s.nameRow}>
                  {!!c.color && <span style={{ ...s.colorDot, background: c.color }} />}
                  <span style={s.cardName}>{c.label}</span>
                  {!!c.isGroup && <span style={s.groupTag}>קטגוריה ראשית</span>}
                  {!c.active && <span style={s.inactiveTag}>מושבתת</span>}
                </div>
              )}
              {/* קטגוריה ראשית, אייקון וצבע - כפי שיוצגו באפליקציה */}
              {editingId === c.id && (
                <div style={s.editExtra}>
                  {!c.isGroup && (
                    <select style={s.editSelect} value={editDraft.parentKey} onChange={(e) => setEditDraft((p) => ({ ...p, parentKey: e.target.value }))}>
                      <option value="">ללא קטגוריה ראשית (מוצגת בשורה הראשית)</option>
                      {groups.map((g) => <option key={g.key} value={g.key}>תחת: {g.label}</option>)}
                    </select>
                  )}
                  <div style={s.editRow}>
                    <select style={{ ...s.editSelect, flex: 1 }} value={editDraft.icon} onChange={(e) => setEditDraft((p) => ({ ...p, icon: e.target.value }))}>
                      {ICON_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <input
                      type="color"
                      style={s.colorInput}
                      value={editDraft.color || '#A6552A'}
                      onChange={(e) => setEditDraft((p) => ({ ...p, color: e.target.value.toUpperCase() }))}
                      title="צבע"
                    />
                    {!!editDraft.color && (
                      <button type="button" style={s.clearColorBtn} onClick={() => setEditDraft((p) => ({ ...p, color: '' }))}>ללא צבע</button>
                    )}
                  </div>
                </div>
              )}
              <span style={s.cardMeta}>
                {c.businessCount ?? 0} בתי עסק · סדר {c.sortOrder ?? 0}
                {!c.isGroup && (c.parentKey ? ` · תחת: ${groupLabel(c.parentKey)}` : ' · עצמאית')}
                {!!c.icon && ` · אייקון: ${iconLabel(c.icon)}`}
              </span>

              <div style={s.cardActions}>
                {editingId === c.id ? (
                  <>
                    <button style={s.saveBtn} onClick={() => saveEdit(c.id)} disabled={busyId === c.id}>{busyId === c.id ? '...' : 'שמירה'}</button>
                    <button style={s.cancelBtn} onClick={() => setEditingId(null)}>ביטול</button>
                  </>
                ) : (
                  <>
                    <button style={s.editBtn} onClick={() => startEdit(c)}>✏️ עריכה</button>
                    <button
                      style={c.active ? s.deactivateBtn : s.activateBtn}
                      onClick={() => onToggleClick(c)}
                      disabled={busyId === c.id}
                    >
                      {busyId === c.id ? '...' : c.active ? 'השבתה' : 'הפעלה'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {addModalOpen && (
        <div style={s.overlay} onClick={() => !adding && setAddModalOpen(false)}>
          <div style={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={s.modalTitle}>קטגוריה חדשה</h3>
            {addError && <div style={s.errorBanner}>❌ {addError}</div>}
            <input
              type="text"
              placeholder="שם הקטגוריה (למשל: יודאיקה)"
              style={s.addInput}
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCategory()}
              maxLength={50}
              autoFocus
            />
            <label style={s.checkRow}>
              <input type="checkbox" checked={newIsGroup} onChange={(e) => setNewIsGroup(e.target.checked)} />
              קטגוריה ראשית (מקבצת תתי קטגוריות, לא משויכת לבתי עסק ישירות)
            </label>
            {!newIsGroup && (
              <select style={s.addInput} value={newParentKey} onChange={(e) => setNewParentKey(e.target.value)}>
                <option value="">ללא קטגוריה ראשית</option>
                {groups.map((g) => <option key={g.key} value={g.key}>תחת: {g.label}</option>)}
              </select>
            )}
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setAddModalOpen(false)} disabled={adding}>ביטול</button>
              <button style={s.saveBtn} onClick={addCategory} disabled={adding}>{adding ? '...' : 'הוספה'}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        visible={!!confirmDeactivate}
        title="השבתת קטגוריה"
        message={confirmDeactivate ? `לקטגוריה "${confirmDeactivate.label}" משויכים ${confirmDeactivate.businessCount} בתי עסק. אחרי ההשבתה היא לא תוצג באפליקציה ולא תהיה זמינה לבחירה, אבל בתי העסק עצמם ימשיכו להופיע. להשבית?` : ''}
        confirmLabel="השבתה"
        danger
        onConfirm={() => toggleActive(confirmDeactivate)}
        onCancel={() => setConfirmDeactivate(null)}
      />
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 12 },
  headerRight:  { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  subtitle:     { margin: '0 0 18px', color: '#697386', fontSize: 13 },
  toolbar:      { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 },
  searchInput:  { flex: 1, minWidth: 180, maxWidth: 320, padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 14, boxSizing: 'border-box', direction: 'rtl', fontFamily: 'inherit' },
  sortToggle:   { display: 'flex', border: '1.5px solid #e3e8ee', borderRadius: 10, overflow: 'hidden' },
  sortBtn:      { padding: '8px 14px', background: '#fff', color: '#4f566b', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  sortBtnActive:{ background: '#1f6feb', color: '#fff' },
  totalBadge:   { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  emptyState:   { textAlign: 'center', padding: '60px 0', color: '#697386', fontWeight: 600 },

  addInput: { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 14, boxSizing: 'border-box', marginBottom: 16 },
  addBtn:   { padding: '10px 20px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100, padding: 16, direction: 'rtl',
  },
  modalCard: {
    width: '100%', maxWidth: 380, background: '#fff', borderRadius: 20,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: 22,
  },
  modalTitle: { margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1a1f36' },
  modalActions: { display: 'flex', gap: 10 },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  card: {
    background: '#fff', borderRadius: 14, border: '1px solid #e3e8ee', padding: 16,
    display: 'flex', flexDirection: 'column', gap: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
  },
  cardInactive: { opacity: 0.6 },
  colorDot: { width: 12, height: 12, borderRadius: 6, display: 'inline-block' },
  groupTag: { fontSize: 11, fontWeight: 700, color: '#1f6feb', background: '#EEF2FF', borderRadius: 6, padding: '2px 8px' },
  editExtra: { display: 'flex', flexDirection: 'column', gap: 6 },
  editSelect: { padding: '6px 8px', borderRadius: 8, border: '1.5px solid #1f6feb', fontSize: 13, fontFamily: 'inherit', direction: 'rtl', minWidth: 0 },
  colorInput: { width: 40, height: 32, padding: 0, border: '1.5px solid #e3e8ee', borderRadius: 8, cursor: 'pointer' },
  clearColorBtn: { padding: '4px 8px', background: '#fff', color: '#697386', border: '1px solid #e3e8ee', borderRadius: 8, cursor: 'pointer', fontSize: 12 },
  checkRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4f566b', marginBottom: 12, cursor: 'pointer' },
  nameRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  cardName: { fontSize: 15, fontWeight: 700, color: '#1a1f36' },
  inactiveTag: { fontSize: 11, fontWeight: 700, color: '#d93838', background: '#fff1f1', borderRadius: 6, padding: '2px 8px' },
  cardMeta: { fontSize: 12, color: '#a3acb9' },
  cardActions: { display: 'flex', gap: 8, marginTop: 4 },
  editRow: { display: 'flex', gap: 6 },
  editInput: { padding: '6px 10px', borderRadius: 8, border: '1.5px solid #1f6feb', fontSize: 14, boxSizing: 'border-box', minWidth: 0 },

  editBtn:      { flex: 1, padding: '7px 10px', background: '#EEF2FF', color: '#1f6feb', border: '1px solid #c7d7fb', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  saveBtn:      { flex: 1, padding: '7px 10px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  cancelBtn:    { flex: 1, padding: '7px 10px', background: '#fff', color: '#697386', border: '1px solid #e3e8ee', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  activateBtn:  { flex: 1, padding: '7px 10px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  deactivateBtn:{ flex: 1, padding: '7px 10px', background: '#fff1f1', color: '#d93838', border: '1px solid #fbc4c4', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
};

export default BusinessCategoriesTab;
